import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { isSafari, isRedirectLoop, clearRedirectHistory } from '@/utils/browserUtils';
import { isSafariRedirectBlocked, clearSafariRedirectProtection, emergencySafariRedirectRecovery } from '@/utils/safariRedirectFix';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw } from 'lucide-react';

/**
 * Fallback component for Safari redirect loop issues
 */
const SafariRedirectFallback = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showFallback, setShowFallback] = useState(false);

  useEffect(() => {
    // Only show fallback for Safari and when there's a potential redirect loop
    if (isSafari() && !loading) {
      // Check immediately if Safari redirect protection is active
      if (isSafariRedirectBlocked()) {
        setShowFallback(true);
        return;
      }

      const timer = setTimeout(() => {
        if (isRedirectLoop(location.pathname) || isSafariRedirectBlocked()) {
          setShowFallback(true);
        }
      }, 2000); // Reduced time to 2 seconds for faster response

      return () => clearTimeout(timer);
    }
  }, [location.pathname, loading]);

  const handleManualNavigation = (path: string) => {
    clearRedirectHistory();
    clearSafariRedirectProtection();
    setShowFallback(false);
    navigate(path, { replace: true });
  };

  const handleRefresh = () => {
    clearRedirectHistory();
    clearSafariRedirectProtection();
    window.location.reload();
  };

  const handleEmergencyRecovery = () => {
    emergencySafariRedirectRecovery();
  };

  if (!showFallback || loading) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-2" />
          <CardTitle>Navigation Issue Detected</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600 text-center">
            We detected a potential navigation issue. Please choose where you'd like to go:
          </p>
          
          <div className="space-y-2">
            {user?.isAdmin && (
              <Button 
                onClick={() => handleManualNavigation('/new-admin')}
                className="w-full"
                variant="outline"
              >
                Admin Dashboard
              </Button>
            )}
            
            {user?.isDoctor && !user.isPendingApproval && (
              <Button 
                onClick={() => handleManualNavigation('/doctor')}
                className="w-full"
                variant="outline"
              >
                Doctor Dashboard
              </Button>
            )}
            
            {user?.isDoctor && user.isPendingApproval && (
              <Button 
                onClick={() => handleManualNavigation('/pending-approval')}
                className="w-full"
                variant="outline"
              >
                Pending Approval
              </Button>
            )}
            
            {user && !user.isAdmin && !user.isDoctor && (
              <Button 
                onClick={() => handleManualNavigation('/dashboard')}
                className="w-full"
                variant="outline"
              >
                Patient Dashboard
              </Button>
            )}
            
            {!user && (
              <>
                <Button 
                  onClick={() => handleManualNavigation('/login')}
                  className="w-full"
                  variant="outline"
                >
                  Login
                </Button>
                <Button 
                  onClick={() => handleManualNavigation('/')}
                  className="w-full"
                  variant="outline"
                >
                  Home Page
                </Button>
              </>
            )}
          </div>
          
          <div className="pt-4 border-t space-y-2">
            <Button
              onClick={handleRefresh}
              className="w-full"
              variant="secondary"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh Page
            </Button>

            {isSafariRedirectBlocked() && (
              <Button
                onClick={handleEmergencyRecovery}
                className="w-full"
                variant="destructive"
                size="sm"
              >
                Emergency Recovery
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SafariRedirectFallback;
